import React, { useEffect, useMemo, useRef, useState } from "react";
import { customersAPI } from "../../api/client";

export default function CustomerSelect({ value, onChange, disabled = false }) {
  const [customers, setCustomers] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const resp = await customersAPI.getAll();
        setCustomers(resp.data || []);
      } catch (err) {
        setError(err.response?.data?.message || "Failed to load customers");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return customers;
    return customers.filter((customer) =>
      customer.name.toLowerCase().includes(q),
    );
  }, [customers, query]);

  const selectedCustomer = customers.find((customer) => customer.id === value);
  const showAdd =
    query.trim().length > 0 &&
    !customers.some(
      (customer) => customer.name.toLowerCase() === query.trim().toLowerCase(),
    );

  const createInline = async () => {
    try {
      const resp = await customersAPI.create(query.trim());
      const created = resp.data;
      setCustomers((prev) => [...prev, { ...created, projectCount: 0 }]);
      onChange(created.id);
      setQuery(created.name);
      setOpen(false);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to create customer");
    }
  };

  return (
    <div ref={containerRef} style={{ position: "relative" }}>
      <input
        type="text"
        value={query || selectedCustomer?.name || ""}
        onChange={(e) => {
          setQuery(e.target.value);
          if (value) onChange("");
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        placeholder="Search or add customer..."
        disabled={disabled}
        autoComplete="off"
      />
      {open && (
        <div className="customer-select-dropdown">
          {loading && <div className="small-muted" style={{ padding: "8px 12px" }}>Loading customers...</div>}
          {!loading && filtered.length === 0 && !showAdd && (
            <div className="small-muted" style={{ padding: "8px 12px" }}>No customers found</div>
          )}
          {!loading &&
            filtered.map((customer) => (
              <button
                key={customer.id}
                className="ts-dropdown-item"
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  onChange(customer.id);
                  setQuery(customer.name);
                  setOpen(false);
                }}
              >
                {customer.name}
              </button>
            ))}
          {!loading && showAdd && (
            <button
              className="ts-dropdown-item"
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={createInline}
            >
              + Add "{query.trim()}" as new customer
            </button>
          )}
          {error && (
            <div className="small-muted" style={{ color: "#b91c1c", padding: "8px 12px" }}>
              {error}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
